import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, TrendingUp, Award, Calendar, Flame
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";

// Dandelion emoji states
const DANDELION_MOODS = [
  { level: 1, emoji: "🥀", label: "Very Low", color: "text-gray-500", bgColor: "bg-gray-100 dark:bg-gray-800" },
  { level: 2, emoji: "🌱", label: "Low", color: "text-amber-600", bgColor: "bg-amber-50 dark:bg-amber-900/20" },
  { level: 3, emoji: "🌼", label: "Neutral", color: "text-yellow-600", bgColor: "bg-yellow-50 dark:bg-yellow-900/20" },
  { level: 4, emoji: "🌻", label: "Good", color: "text-green-600", bgColor: "bg-green-50 dark:bg-green-900/20" },
  { level: 5, emoji: "🌾", label: "Vibrant", color: "text-emerald-600", bgColor: "bg-emerald-50 dark:bg-emerald-900/20" }
];

const MoodProgressPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewDays, setViewDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [viewDays]);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/mood/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mood/history?days=${viewDays}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setHistory(historyRes.data.entries);
    } catch (error) {
      console.error("Error fetching mood data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMoodInfo = (level) => {
    return DANDELION_MOODS.find(m => m.level === level) || DANDELION_MOODS[2];
  };

  const getAverageLabel = (avg) => {
    if (!avg) return "No data";
    if (avg < 1.5) return "Very Low";
    if (avg < 2.5) return "Low";
    if (avg < 3.5) return "Neutral";
    if (avg < 4.5) return "Good";
    return "Vibrant";
  };

  // Create chart data for last N days
  const chartData = () => {
    const days = [];
    const today = new Date();
    
    for (let i = viewDays - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const entry = history.find(h => h.date === dateStr);
      
      days.push({
        date: dateStr,
        dayLabel: date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' }),
        mood: entry?.mood_level || null,
        note: entry?.note || null
      });
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  const data = chartData();

  return (
    <div className="min-h-screen hero-gradient" data-testid="mood-progress-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Mood Progress</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Your Emotional Journey
          </h1>
          <p className="text-lg text-muted-foreground">
            Track your emotional wellbeing over time
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {/* Current Streak */}
          <Card className="glass-card rounded-2xl border-0">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-2">
                <Flame className="w-6 h-6 text-orange-500" />
              </div>
              <p className="text-2xl font-bold text-foreground">{stats?.current_streak || 0}</p>
              <p className="text-xs text-muted-foreground">Day Streak</p>
            </CardContent>
          </Card>

          {/* 7-Day Average */}
          <Card className="glass-card rounded-2xl border-0">
            <CardContent className="p-4 text-center">
              <div className="text-3xl mb-2">
                {stats?.average_mood_7d ? getMoodInfo(Math.round(stats.average_mood_7d)).emoji : "—"}
              </div>
              <p className="text-lg font-semibold text-foreground">
                {stats?.average_mood_7d?.toFixed(1) || "—"}
              </p>
              <p className="text-xs text-muted-foreground">7-Day Avg</p>
            </CardContent>
          </Card>

          {/* 30-Day Average */}
          <Card className="glass-card rounded-2xl border-0">
            <CardContent className="p-4 text-center">
              <div className="text-3xl mb-2">
                {stats?.average_mood_30d ? getMoodInfo(Math.round(stats.average_mood_30d)).emoji : "—"}
              </div>
              <p className="text-lg font-semibold text-foreground">
                {stats?.average_mood_30d?.toFixed(1) || "—"}
              </p>
              <p className="text-xs text-muted-foreground">30-Day Avg</p>
            </CardContent>
          </Card>

          {/* Total Entries */}
          <Card className="glass-card rounded-2xl border-0">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mx-auto mb-2">
                <Calendar className="w-6 h-6 text-teal-600" />
              </div>
              <p className="text-2xl font-bold text-foreground">{stats?.total_entries || 0}</p>
              <p className="text-xs text-muted-foreground">Total Days</p>
            </CardContent>
          </Card>
        </div>

        {/* View Toggle */}
        <div className="flex justify-center gap-2 mb-6">
          <Button
            variant={viewDays === 7 ? "default" : "outline"}
            onClick={() => setViewDays(7)}
            className="rounded-full"
          >
            7 Days
          </Button>
          <Button
            variant={viewDays === 30 ? "default" : "outline"}
            onClick={() => setViewDays(30)}
            className="rounded-full"
          >
            30 Days
          </Button>
        </div>

        {/* Mood Chart */}
        <Card className="glass-card rounded-2xl border-0 mb-10">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-6">
              {viewDays}-Day Mood Trend
            </h3>
            
            {/* Simple Bar Chart */}
            <div className="flex items-end justify-between gap-1 h-48 mb-4">
              {data.map((day, i) => {
                const moodInfo = day.mood ? getMoodInfo(day.mood) : null;
                const height = day.mood ? `${(day.mood / 5) * 100}%` : '0%';
                
                return (
                  <div 
                    key={i} 
                    className="flex-1 flex flex-col items-center justify-end h-full group relative"
                  >
                    {day.mood ? (
                      <div 
                        className={`w-full rounded-t-lg transition-all ${moodInfo.bgColor} hover:opacity-80`}
                        style={{ height }}
                      >
                        <div className="text-center pt-1 text-lg">
                          {moodInfo.emoji}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-4 bg-muted/30 rounded-t-lg" />
                    )}
                    
                    {/* Tooltip */}
                    {day.note && (
                      <div className="absolute bottom-full mb-2 hidden group-hover:block bg-foreground text-background text-xs p-2 rounded max-w-32 z-10">
                        {day.note}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Day Labels */}
            <div className="flex justify-between gap-1">
              {data.map((day, i) => (
                <div key={day.date || `label-${i}`} className="flex-1 text-center">
                  <span className="text-xs text-muted-foreground">
                    {viewDays <= 7 ? day.dayLabel : (i % 5 === 0 ? day.dayLabel : '')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Badges */}
        {stats?.badges && stats.badges.length > 0 && (
          <Card className="glass-card rounded-2xl border-0 mb-10">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Earned Badges
              </h3>
              <div className="flex flex-wrap gap-3">
                {stats.badges.map((badge) => (
                  <div 
                    key={badge}
                    className="bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 px-4 py-2 rounded-full flex items-center gap-2"
                  >
                    <Award className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700 dark:text-amber-400">{badge}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mood Legend */}
        <Card className="glass-card rounded-2xl border-0">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
              Mood Scale
            </h3>
            <div className="flex justify-between">
              {DANDELION_MOODS.map((mood) => (
                <div key={mood.level} className="text-center">
                  <div className="text-3xl mb-1">{mood.emoji}</div>
                  <p className={`text-xs font-medium ${mood.color}`}>{mood.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Encouragement */}
        {!stats?.logged_today && (
          <div className="text-center mt-10">
            <p className="text-muted-foreground mb-4">
              You haven't logged your mood today yet!
            </p>
            <Button 
              onClick={() => navigate("/dashboard")}
              className="btn-primary"
            >
              Log Today's Mood
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default MoodProgressPage;
